import { FC, useEffect, useRef, useState } from "react";
import "./Account.scss";
import MainNavbar from "../../../components/MainNavbar/MainNavbar";
import ProfileLayout from "../../../components/ProfileLayout/ProfileLayout";
import { useAuth } from "../../../providers/AuthProvider";
import { ProfileService } from "../../../services/Profile/ProfileService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { BvIcon } from "bevi-icon";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { UserRoleEnum } from "../../../enums/User/UserRoleEnum";
const getInitials = (name?: string) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

interface AccountProps {}

const ACCEPTED_FORMATS = ["image/png", "image/jpeg", "image/webp"];
const ACCEPTED_EXTENSIONS = ".png,.jpg,.jpeg,.webp";

const Account: FC<AccountProps> = () => {
  const { userLogged, setUserLogged, profileImageUrl, refreshProfileImage } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useMediaQuery({ maxWidth: 426 });

  const [userData, setUserData] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editName, setEditName] = useState<string>("");
  const [editEmail, setEditEmail] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const [isRemovingImage, setIsRemovingImage] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (userLogged && userLogged.role !== UserRoleEnum.ADMIN) {
      navigate("/", { replace: true });
    }
  }, [userLogged]);

  const profileService = new ProfileService();

  const fetchUserData = async () => {
    setIsLoadingData(true);
    const [response, error] = await profileService.me();
    setIsLoadingData(false);

    if (error || !response) {
      toast.error("Erro ao carregar dados do usuário.");
      return;
    }

    setUserData(response?.data?.user ?? response?.data ?? response);
  };

  useEffect(() => {
    fetchUserData();
  }, []);


  const handleAlterarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_FORMATS.includes(file.type)) {
      toast.error("Formato inválido. Aceitos: .png, .jpg, .jpeg, .webp");
      e.target.value = "";
      return;
    }

    setIsUploadingImage(true);
    const [, error] = await profileService.uploadProfileImage(file);
    setIsUploadingImage(false);
    e.target.value = "";

    if (error) {
      toast.error("Erro ao fazer upload da imagem.");
      return;
    }

    toast.success("Foto de perfil atualizada com sucesso!");
    await Promise.all([fetchUserData(), refreshProfileImage()]);
  };

  const handleRemoverClick = () => {
    Swal.fire({
      title: "<strong>Atenção</strong>",
      html: "<span>Deseja remover a foto de perfil?</span>",
      icon: "question",
      confirmButtonText: "Sim, remover!",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      setIsRemovingImage(true);
      const [, error] = await profileService.deleteProfileImage();
      setIsRemovingImage(false);

      if (error) {
        toast.error("Erro ao remover a foto de perfil.");
        return;
      }

      toast.success("Foto de perfil removida com sucesso!");
      await Promise.all([fetchUserData(), refreshProfileImage()]);
    });
  };

  const handleEditClick = () => {
    setEditName(userData?.name ?? "");
    setEditEmail(userData?.email ?? "");
    setIsEditing(true);
  };

  const handleCancelEdit = () => setIsEditing(false);

  const handleSave = async () => {
    setIsSaving(true);
    const [, error] = await profileService.updateUser({
      id: userData?.id,
      name: editName,
      email: editEmail,
    });
    setIsSaving(false);

    if (error) {
      toast.error("Erro ao salvar os dados.");
      return;
    }

    toast.success("Dados atualizados com sucesso!");
    setIsEditing(false);

    if (userLogged) {
      setUserLogged({ ...userLogged, name: editName });
    }

    await fetchUserData();
  };

  const hasImage = !!profileImageUrl;

  const renderAvatarLg = () => {
    const avatarImage = hasImage ? (
      <img
        src={profileImageUrl!}
        alt="Foto de perfil"
        className="account-avatar-lg object-fit-cover"
      />
    ) : (
      <div className="account-avatar-lg account-avatar-placeholder d-flex align-items-center justify-content-center">
        <span className="account-initials">{getInitials(userLogged?.name)}</span>
      </div>
    );

    if (isMobile) {
      return (
        <div className="account-avatar-wrapper">
          <div
            className="account-avatar-clickable"
            onClick={handleAlterarClick}
            title="Alterar foto"
          >
            {avatarImage}
            <div className="account-avatar-overlay">
              <BvIcon name="editSquare" variant="solid" weight={600} width={24} height={24} color="white"/>
            </div>
          </div>

          {hasImage && (
            <button
              className="account-avatar-remove"
              onClick={handleRemoverClick}
              disabled={isUploadingImage || isRemovingImage}
              title="Remover foto"
            >
              {isRemovingImage ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <span>&times;</span>
              )}
            </button>
          )}

          {isUploadingImage && (
            <div className="account-avatar-overlay account-avatar-overlay--loading">
              <span className="spinner-border spinner-border-sm text-white" />
            </div>
          )}
        </div>
      );
    }

    return avatarImage;
  };

  return (
    <>
      <MainNavbar />
      <div className="Account" data-testid="Account">
        <div className="account-card">
        <ProfileLayout profileImageUrl={profileImageUrl}>
          <div className="account-content-inner">
          <h5 className="mb-4 title div-title">Conta</h5>

          {isLoadingData ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Photo Section */}
              <div className="mb-4">
                <div className="fw-bold text-sm mb-2 info-label div-title">Foto</div>
                <div className="d-flex align-items-center gap-3 picture">
                  <div className="div-picture">
                    {renderAvatarLg()}
                  </div>
                  <div className="d-flex gap-2 div-title">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPTED_EXTENSIONS}
                      className="d-none"
                      onChange={handleFileChange}
                    />
                    <button
                      className="btn btn-outline-secondary btn-sm btn-picture"
                      onClick={handleAlterarClick}
                      disabled={isUploadingImage || isRemovingImage}
                    >
                      {isUploadingImage ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          Enviando...
                        </>
                      ) : (
                        "Alterar"
                      )}
                    </button>

                    {hasImage && (
                      <button
                        className="btn btn-outline-secondary btn-sm btn-picture"
                        onClick={handleRemoverClick}
                        disabled={isUploadingImage || isRemovingImage}
                      >
                        {isRemovingImage ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" />
                            Removendo...
                          </>
                        ) : (
                          "Remover"
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* User Data */}
              <div className="row g-4 mb-4">
                <div className="col-md-6">
                  <div className="account-field-label info-label">Nome</div>
                  {isEditing ? (
                    <input
                      type="text"
                      className="form-control form-control-sm mt-1"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  ) : (
                    <div className="account-field-value info">{userData?.name ?? "—"}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <div className="account-field-label info-label">E-mail</div>
                  {isEditing ? (
                    <input
                      type="email"
                      className="form-control form-control-sm mt-1"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                    />
                  ) : (
                    <div className="account-field-value info">{userData?.email ?? "—"}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <div className="account-field-label info-label">Código Parceiro</div>
                  <div className="account-field-value info">
                    {userData?.partner_code ?? userLogged?.partner_code ?? "—"}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="account-field-label info-label">Tipo de Usuário</div>
                  <div className="account-field-value info">
                    {userData?.access_profile?.name ?? "—"}
                  </div>
                </div>
              </div>
            </>
          )}
          </div>
        </ProfileLayout>
        </div>
      </div>
    </>
  );
};

export default Account;
